import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

export default function Pricing() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setCfg(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = {
        ...cfg,
        price_per_kg: Number(cfg.price_per_kg),
        steel_density: Number(cfg.steel_density),
        panel_length_extra_mm: Number(cfg.panel_length_extra_mm),
        base_plate_weight_kg: Number(cfg.base_plate_weight_kg),
        uprights_per_rack: parseInt(cfg.uprights_per_rack),
        angled_support_spacing_mm: Number(cfg.angled_support_spacing_mm),
        angled_support_angle_factor: Number(cfg.angled_support_angle_factor),
        depth_vertical_breakpoints_mm: (cfg.depth_vertical_breakpoints_mm || []).map(Number),
        depth_vertical_extra_step_mm: Number(cfg.depth_vertical_extra_step_mm),
        gst_percent: Number(cfg.gst_percent),
        company_gstin: cfg.company_gstin || "",
        thickness_prices: cfg.thickness_prices.map((t) => ({
          thickness: Number(t.thickness),
          price_per_kg: Number(t.price_per_kg),
        })),
      };
      const { data } = await api.put("/pricing", payload);
      setCfg(data);
      toast.success("Pricing updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!cfg) {
    return (
      <Layout>
        <div className="p-10 label-tech">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin · Pricing"
        title="Pricing Configuration"
        subtitle="Base rate, per-thickness overrides, steel density & formula constants."
        actions={
          <button onClick={save} className="btn-primary" data-testid="save-pricing">
            <Save size={16} /> Save changes
          </button>
        }
      />

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="panel p-6 space-y-5" data-testid="base-pricing">
          <div className="label-tech">Formula constants</div>
          <Field
            label="Steel density (kg/mm³)"
            value={cfg.steel_density}
            onChange={(v) => setCfg({ ...cfg, steel_density: v })}
            testid="field-density"
            step="any"
          />
          <Field
            label="Panel length extra (mm)"
            value={cfg.panel_length_extra_mm}
            onChange={(v) => setCfg({ ...cfg, panel_length_extra_mm: v })}
            testid="field-panel-extra"
          />
          <Field
            label="Base plate weight per vertical (kg)"
            value={cfg.base_plate_weight_kg}
            onChange={(v) => setCfg({ ...cfg, base_plate_weight_kg: v })}
            testid="field-base-plate"
            step="any"
          />
          <Field
            label="Uprights per rack"
            value={cfg.uprights_per_rack}
            onChange={(v) => setCfg({ ...cfg, uprights_per_rack: v })}
            testid="field-uprights"
          />
          <Field
            label="Angled support spacing (mm)"
            value={cfg.angled_support_spacing_mm}
            onChange={(v) => setCfg({ ...cfg, angled_support_spacing_mm: v })}
            testid="field-angle-spacing"
          />
          <Field
            label="Angled support angle factor (√2 ≈ 1.4142 for 45°)"
            value={cfg.angled_support_angle_factor}
            onChange={(v) => setCfg({ ...cfg, angled_support_angle_factor: v })}
            testid="field-angle-factor"
            step="any"
          />
          <div>
            <label className="label-tech mb-1 block">
              Depth vertical breakpoints (mm, comma-separated)
            </label>
            <input
              className="input-tech mono"
              value={(cfg.depth_vertical_breakpoints_mm || []).join(", ")}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  depth_vertical_breakpoints_mm: e.target.value
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean)
                    .map(Number)
                    .filter((n) => !Number.isNaN(n)),
                })
              }
              data-testid="field-depth-breakpoints"
            />
            <div className="text-[0.7rem] text-ink-muted mt-1.5">
              Depth ≤ 1st → 2 verticals/side, ≤ 2nd → 3, … +1 per extra step beyond last.
            </div>
          </div>
          <Field
            label="Extra-step beyond last breakpoint (mm)"
            value={cfg.depth_vertical_extra_step_mm}
            onChange={(v) => setCfg({ ...cfg, depth_vertical_extra_step_mm: v })}
            testid="field-depth-extra-step"
          />
          <Field
            label="GST percent (%)"
            value={cfg.gst_percent}
            onChange={(v) => setCfg({ ...cfg, gst_percent: v })}
            testid="field-gst-percent"
            step="any"
          />
          <div>
            <label className="label-tech mb-1 block">Company GSTIN</label>
            <input
              className="input-tech mono"
              value={cfg.company_gstin || ""}
              onChange={(e) => setCfg({ ...cfg, company_gstin: e.target.value })}
              placeholder="e.g. 07ABCDE1234F1Z5"
              data-testid="field-company-gstin"
            />
          </div>
          <div>
            <label className="label-tech mb-1 block">Currency</label>
            <input
              className="input-tech mono"
              value={cfg.currency}
              onChange={(e) => setCfg({ ...cfg, currency: e.target.value })}
            />
          </div>
          <div className="text-xs text-ink-muted pt-3 border-t border-line">
            Last updated: <span className="mono">{cfg.updated_at}</span>
          </div>
        </div>

        <div className="panel p-6" data-testid="thickness-pricing">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-tech">Thickness-wise pricing (₹ / kg)</div>
              <div className="text-[0.7rem] text-ink-muted mt-1 normal-case tracking-normal">
                Rate used = upright rate if beam & upright thickness match;
                otherwise the mean of the two. Applied to the entire rack weight.
                Panel thickness does not affect pricing.
              </div>
            </div>
            <button
              onClick={() =>
                setCfg({
                  ...cfg,
                  thickness_prices: [
                    ...cfg.thickness_prices,
                    { thickness: 1.6, price_per_kg: 91 },
                  ],
                })
              }
              className="btn-ghost text-xs"
              data-testid="add-thickness"
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {cfg.thickness_prices.length === 0 ? (
            <div className="text-sm text-ink-muted">
              No thickness prices set. Click <span className="font-bold">Add</span> to add one
              (e.g. 1.6 mm → ₹91, 2.0 mm → ₹88).
            </div>
          ) : (
            <table className="w-full grid-lines">
              <thead>
                <tr>
                  <th className="num">Thickness (mm)</th>
                  <th className="num">Price / kg (₹)</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {cfg.thickness_prices.map((t, i) => (
                  <tr key={i}>
                    <td className="num">
                      <input
                        className="input-tech mono text-right"
                        type="number"
                        step="any"
                        value={t.thickness}
                        onChange={(e) => {
                          const arr = [...cfg.thickness_prices];
                          arr[i] = { ...arr[i], thickness: e.target.value };
                          setCfg({ ...cfg, thickness_prices: arr });
                        }}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="input-tech mono text-right"
                        type="number"
                        step="any"
                        value={t.price_per_kg}
                        onChange={(e) => {
                          const arr = [...cfg.thickness_prices];
                          arr[i] = { ...arr[i], price_per_kg: e.target.value };
                          setCfg({ ...cfg, thickness_prices: arr });
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn-ghost text-brand-danger"
                        onClick={() =>
                          setCfg({
                            ...cfg,
                            thickness_prices: cfg.thickness_prices.filter((_, j) => j !== i),
                          })
                        }
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, testid, step }) {
  return (
    <div>
      <label className="label-tech mb-1 block">{label}</label>
      <input
        type="number"
        step={step || "any"}
        className="input-tech mono"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
